import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Shield, FileText, AlertTriangle, CheckCircle, XCircle, Download, Plus, Eye, Clock, Scale, RefreshCw } from 'lucide-react';
import api from '../../lib/api';

const DEPOSIT_SCHEMES = [
  { value: 'DPS', label: 'Deposit Protection Service' },
  { value: 'TDS', label: 'Tenancy Deposit Scheme' },
  { value: 'mydeposits', label: 'mydeposits' },
];

const CASE_STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  tenant_response_pending: 'bg-yellow-100 text-yellow-800',
  landlord_response_pending: 'bg-orange-100 text-orange-800',
  disputed: 'bg-red-100 text-red-800',
  resolved_tenant_favour: 'bg-green-100 text-green-800',
  resolved_landlord_favour: 'bg-purple-100 text-purple-800',
  resolved_split: 'bg-teal-100 text-teal-800',
  closed: 'bg-gray-100 text-gray-800',
};

export default function DepositLifecycle() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('submissions');
  const [submissions, setSubmissions] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [caseDialogOpen, setCaseDialogOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [formData, setFormData] = useState({
    scheme: 'DPS',
    submission_type: 'csv',
    deposit_ids: [],
  });
  const [caseFormData, setCaseFormData] = useState({
    deposit_id: '',
    case_type: 'deduction_proposal',
    deductions: [{ category: '', description: '', amount: 0 }],
    response_deadline_days: 14,
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'submissions') {
        const response = await api.get('/finance/deposits/submissions');
        setSubmissions(response.data);
      } else {
        const response = await api.get('/finance/deposits/cases');
        setCases(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubmission = async (e) => {
    e.preventDefault();
    try {
      await api.post('/finance/deposits/submissions', formData);
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error creating submission:', error);
    }
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    try {
      await api.post('/finance/deposits/cases', caseFormData);
      setCaseDialogOpen(false);
      resetCaseForm();
      fetchData();
    } catch (error) {
      console.error('Error creating case:', error);
    }
  };

  const handleExportCSV = async (submissionId) => {
    try {
      await api.post(`/finance/deposits/submissions/${submissionId}/export-csv`);
      alert('CSV export generated. Download via files endpoint.');
      fetchData();
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  const handleMarkSubmitted = async (submissionId) => {
    const reference = window.prompt('Enter scheme reference number:');
    if (reference !== null) {
      try {
        await api.post(`/finance/deposits/submissions/${submissionId}/mark-submitted?submission_reference=${reference}`);
        fetchData();
      } catch (error) {
        console.error('Error marking submitted:', error);
      }
    }
  };

  const handleSubmitCase = async (caseId) => {
    try {
      await api.post(`/finance/deposits/cases/${caseId}/submit`);
      fetchData();
    } catch (error) {
      console.error('Error submitting case:', error);
    }
  };

  const viewCaseDetails = async (caseItem) => {
    try {
      const response = await api.get(`/finance/deposits/cases/${caseItem.id}`);
      setSelectedCase(response.data);
    } catch (error) {
      console.error('Error fetching case details:', error);
    }
  };

  const resetCaseForm = () => {
    setCaseFormData({
      deposit_id: '',
      case_type: 'deduction_proposal',
      deductions: [{ category: '', description: '', amount: 0 }],
      response_deadline_days: 14,
    });
  };

  const addDeduction = () => {
    setCaseFormData({
      ...caseFormData,
      deductions: [...caseFormData.deductions, { category: '', description: '', amount: 0 }],
    });
  };

  const updateDeduction = (index, field, value) => {
    const newDeductions = [...caseFormData.deductions];
    newDeductions[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setCaseFormData({ ...caseFormData, deductions: newDeductions });
  };

  const removeDeduction = (index) => {
    const newDeductions = caseFormData.deductions.filter((_, i) => i !== index);
    setCaseFormData({ ...caseFormData, deductions: newDeductions });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Deposit Lifecycle</h1>
            <p className="text-gray-500">Manage deposit protection, submissions, and disputes</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="submissions">Scheme Submissions</TabsTrigger>
              <TabsTrigger value="cases">Deduction Cases</TabsTrigger>
            </TabsList>
            <div>
              {activeTab === 'submissions' ? (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button><Plus className="w-4 h-4 mr-2" /> New Submission</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Deposit Submission</DialogTitle>
                      <DialogDescription>
                        Submit deposits to a protection scheme.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateSubmission} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Protection Scheme</Label>
                        <Select value={formData.scheme} onValueChange={(v) => setFormData({...formData, scheme: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {DEPOSIT_SCHEMES.map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Deposit IDs (comma separated)</Label>
                        <Input
                          placeholder="Enter deposit IDs"
                          onChange={(e) => setFormData({
                            ...formData,
                            deposit_ids: e.target.value.split(',').map(id => id.trim()).filter(id => id)
                          })}
                        />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button type="submit">Create Submission</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              ) : (
                <Dialog open={caseDialogOpen} onOpenChange={setCaseDialogOpen}>
                  <DialogTrigger asChild>
                    <Button><Plus className="w-4 h-4 mr-2" /> New Case</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Create Deduction Case</DialogTitle>
                      <DialogDescription>
                        Propose deductions from a tenant&apos;s deposit.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateCase} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Deposit ID</Label>
                        <Input
                          value={caseFormData.deposit_id}
                          onChange={(e) => setCaseFormData({...caseFormData, deposit_id: e.target.value})}
                          placeholder="Enter deposit ID"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Case Type</Label>
                        <Select value={caseFormData.case_type} onValueChange={(v) => setCaseFormData({...caseFormData, case_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="deduction_proposal">Deduction Proposal</SelectItem>
                            <SelectItem value="landlord_claim">Landlord Claim</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label>Deductions</Label>
                          <Button type="button" size="sm" variant="outline" onClick={addDeduction}>
                            <Plus className="w-3 h-3 mr-1" /> Add
                          </Button>
                        </div>
                        {caseFormData.deductions.map((d, i) => (
                          <div key={i} className="grid grid-cols-4 gap-2 items-end">
                            <Input
                              placeholder="Category"
                              value={d.category}
                              onChange={(e) => updateDeduction(i, 'category', e.target.value)}
                            />
                            <Input
                              placeholder="Description"
                              value={d.description}
                              onChange={(e) => updateDeduction(i, 'description', e.target.value)}
                            />
                            <Input
                              type="number"
                              placeholder="Amount"
                              value={d.amount}
                              onChange={(e) => updateDeduction(i, 'amount', e.target.value)}
                            />
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeDeduction(i)}>
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <Label>Response Deadline (days)</Label>
                        <Input
                          type="number"
                          min="7"
                          max="30"
                          value={caseFormData.response_deadline_days}
                          onChange={(e) => setCaseFormData({...caseFormData, response_deadline_days: parseInt(e.target.value)})}
                        />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setCaseDialogOpen(false)}>Cancel</Button>
                        <Button type="submit">Create Case</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>

          <TabsContent value="submissions" className="space-y-4">
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Scheme</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Deposits</TableHead>
                      <TableHead>Total Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {submissions.map(sub => (
                      <TableRow key={sub.id}>
                        <TableCell>
                          <Badge variant="outline">{sub.scheme}</Badge>
                        </TableCell>
                        <TableCell className="capitalize">{sub.submission_type}</TableCell>
                        <TableCell>{sub.submission_reference || '-'}</TableCell>
                        <TableCell>{sub.deposit_count}</TableCell>
                        <TableCell>{formatCurrency(sub.total_amount)}</TableCell>
                        <TableCell>
                          <Badge className={
                            sub.status === 'acknowledged' ? 'bg-green-100 text-green-800' :
                            sub.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {sub.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {sub.status === 'pending' && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => handleExportCSV(sub.id)}>
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleMarkSubmitted(sub.id)}>
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {submissions.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No submissions found. Create your first submission to register deposits with a scheme.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="cases" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardContent className="pt-6">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Case #</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Claimed</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Deadline</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cases.map(c => (
                          <TableRow
                            key={c.id}
                            className={selectedCase?.id === c.id ? 'bg-blue-50' : 'cursor-pointer hover:bg-gray-50'}
                            onClick={() => viewCaseDetails(c)}
                          >
                            <TableCell className="font-mono">{c.case_number}</TableCell>
                            <TableCell className="capitalize">{c.case_type.replace(/_/g, ' ')}</TableCell>
                            <TableCell>{formatCurrency(c.total_claimed)}</TableCell>
                            <TableCell>
                              <Badge className={CASE_STATUS_COLORS[c.status] || 'bg-gray-100'}>
                                {c.status.replace(/_/g, ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {c.response_deadline ? (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {c.response_deadline}
                                </span>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {c.status === 'draft' && (
                                <Button size="sm" variant="outline" onClick={(e) => {
                                  e.stopPropagation();
                                  handleSubmitCase(c.id);
                                }}>
                                  Submit
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {cases.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        No deposit cases found.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Case Details Sidebar */}
              <div>
                {selectedCase ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Case Details</CardTitle>
                      <CardDescription>{selectedCase.case_number}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-gray-500">Total Claimed</div>
                          <div className="font-semibold">{formatCurrency(selectedCase.total_claimed)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Agreed</div>
                          <div className="font-semibold">{formatCurrency(selectedCase.amount_agreed)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Disputed</div>
                          <div className="font-semibold">{formatCurrency(selectedCase.amount_disputed)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Status</div>
                          <Badge className={CASE_STATUS_COLORS[selectedCase.status]}>
                            {selectedCase.status.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                      </div>

                      {selectedCase.deductions && selectedCase.deductions.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2">Deductions</h4>
                          <div className="space-y-2">
                            {selectedCase.deductions.map((d, i) => (
                              <div key={i} className="p-2 bg-gray-50 rounded text-sm">
                                <div className="flex justify-between">
                                  <span className="font-medium">{d.category}</span>
                                  <span>{formatCurrency(d.amount)}</span>
                                </div>
                                {d.description && (
                                  <div className="text-gray-500 text-xs mt-1">{d.description}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedCase.events && selectedCase.events.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2">Timeline</h4>
                          <div className="space-y-2">
                            {selectedCase.events.map((event, i) => (
                              <div key={i} className="flex gap-2 text-sm">
                                <div className="w-2 h-2 mt-2 rounded-full bg-blue-500"></div>
                                <div>
                                  <div className="font-medium">{event.event_type.replace(/_/g, ' ')}</div>
                                  <div className="text-gray-500 text-xs">{event.description}</div>
                                  <div className="text-gray-400 text-xs">
                                    {new Date(event.created_at).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center text-gray-500">
                      <Scale className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      Select a case to view details
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
